import { putSynchedBooking } from "@/api/booking/booking";
import { LogLevel, LogService } from "@/services/logging";
import {
  BookingState,
  IUpdatedBooking,
  SyncStatus,
} from "@/types/db/product-slot-booking";
import { cloneDeep } from "lodash";
import BookingModule from "@/store/modules/booking";
import { checkAndSynchUpdatedBookingFiles } from "@/utils/vue-helpers/pos-files";
import UserModule from "@/store/modules/user";
import { SyncType } from "@/interfaces/store/IBooking";
import { dexieDB } from "@/services/dexie-database";
import { createCompositeId } from "@/utils/helpers/app";
import dayjs from "dayjs";
import { initiateOrdersSync, MAX_SYNC_ATTEMPTS } from "@/services/sync-service";
import Timeout from "await-timeout";
import { releaseCancelledSeats } from "@/utils/helpers/seats-io.helper";

export async function saveUpdatedBooking(diffObject: any) {
  // Lets first check if the diffObject is valid
  if (
    !diffObject ||
    !diffObject.booking ||
    !diffObject.booking.uuid ||
    isEmptyBooking(diffObject)
  ) {
    LogService.info(
      `Error: Empty updatedBooking captured, uuid:${diffObject.booking.uuid}`,
      { params: diffObject },
      LogLevel.Error
    );
    return;
  }

  // Build updated booking object
  const diffObjectCopy = cloneDeep(diffObject);
  const creationTime = dayjs().toISOString();
  const updatedBooking: IUpdatedBooking = {
    _id: createCompositeId([creationTime, diffObject.booking.uuid]),
    uuid: diffObject.booking.uuid,
    diffObject: diffObjectCopy,
    syncStatus: SyncStatus.Pending,
    attempts: 0,
  };

  await upsertUpdatedBooking(updatedBooking);

  // Trigger the sync
  initiateOrdersSync();
  return true;
}

function isEmptyBooking(updatedBooking: any): boolean {
  if (updatedBooking.booking.state === BookingState.PendingInfo) {
    if (
      !updatedBooking.accountingUpdates.length &&
      !updatedBooking.accountingEntriesUpdates.length &&
      !updatedBooking.newAccountings.length &&
      !updatedBooking.newAccountingEntries.length &&
      !updatedBooking.newPSS.length &&
      !updatedBooking.pssUpdates.length &&
      !updatedBooking.newBookingCustomFieldsAnswers.length &&
      !updatedBooking.updatedBookingCustomFieldsAnswers.length &&
      !updatedBooking.newPSSCustomFieldsAnswers.length &&
      !updatedBooking.updatedPSSCustomFieldsAnswers.length
    ) {
      return true;
    }
  }
  return false;
}

export async function syncUpdatedBookings(diffObject: IUpdatedBooking[]) {
  // Check if database is open before proceeding
  if (!dexieDB.isOpen()) {
    LogService.info("Database is not open, skipping sync");
    return;
  }

  for (let i = diffObject.length; i >= 0; i--) {
    // Clone objects so that we don't manipulate original data
    const objCopy = cloneDeep(diffObject[i]);
    if (!objCopy) continue;
    try {
      objCopy.attempts = (objCopy.attempts || 0) + 1;
      LogService.info(
        `(compressed) Doing PUT synchronize-bookings of edited booking with id: ${objCopy._id}, attempt: ${objCopy.attempts}`,
        { params: objCopy }
      );
      const res = await putSynchedBooking(objCopy.diffObject);
      //if backend responds that booking is successfully synced, we remove it from the list of pendingSyncBookings

      if (res.status === "success") {
        LogService.info(
          `Sync: Successful PUT synchronize-bookings, id: ${objCopy._id}, attempt: ${objCopy.attempts}`,
          { params: res.data.updatedBooking }
        );
        BookingModule.removeSyncedBookingFromPendingSyncBookings(
          //need to put uuid in the array, because function removeSyncedBookingFromPendingSyncBookings compares two arrays, not strings
          {
            synchedBooking: [res.data.updatedBooking],
            typeToDelete: SyncType.PUT,
          }
        );

        // If bookings synched has custom fields files in them, then we need to insert the files IDs into their DB
        // to trigger their synching process
        LogService.info(
          `Checking if there are files to synch for updated-booking: ${objCopy.uuid}`
        );
        await checkAndSynchUpdatedBookingFiles(objCopy);

        // Mark the booking as synched
        objCopy.syncStatus = SyncStatus.Synced;
        await upsertUpdatedBooking(objCopy);

        // If booking has canceled tickets of type "seating", we need to release the seats for those tickets
        await releaseCancelledSeats(objCopy.diffObject.pssUpdates);
      } else {
        LogService.info(
          `Sync: Invalid backend response when updating booking: ${objCopy.uuid}`,
          { res },
          LogLevel.Error
        );
      }
    } catch (error: any) {
      // If we have a network error then we do nothing, we do not increase attempts
      if (
        error?.networkError?.length ||
        String(error?.message).toLowerCase().includes("network error")
      ) {
        // Do nothing since this is a network error
        LogService.info(`Network Error when PUTting booking: ${objCopy.uuid}`);
      } else {
        // If error is coming from backend logic (i.e: data we posted are correct) then log it and don't try again
        // otherwise, throw the error so that it tries synching again
        const data = error && error?.response && error.response?.data;
        const msg =
          data?.messages && data.messages?.length ? data.messages[0] : "";

        if (
          msg.includes("SMEETZPOS") &&
          // do not ignore put requests that failed because booking doesn't exist already on backend
          !msg.includes("not found")
        ) {
          // log the error and don't try again
          LogService.info(
            `SMEETZPOS Error when PUT booking with id: ${objCopy._id}`,
            { params: { errorMessage: msg } },
            LogLevel.Error
          );

          // Mark the booking as ignored
          objCopy.syncStatus = SyncStatus.Ignored;
        } else {
          if (UserModule.isColleague) {
            // This is a colleague, we log what shouldn't be alerted in Slack
            LogService.info(
              `Error (From Colleague) when PUT booking with id: ${objCopy._id}`,
              { params: { errorMessage: msg } },
              LogLevel.Error
            );
            // Mark the booking as ignored
            objCopy.syncStatus = SyncStatus.Ignored;
          } else {
            // This is not a colleague, we log what should be alerted in Slack
            LogService.info(
              `Error when PUT booking with id: ${objCopy._id}`,
              {
                params: {
                  errorMessage: msg,
                  error: error,
                },
              },
              LogLevel.Error
            );
            // Mark the booking as failed
            objCopy.syncStatus = SyncStatus.Failed;
          }
        }
        await upsertUpdatedBooking(objCopy);
      }
    }
  }
}

/**
 * Save or update updatedBooking in local db
 * @param updatedBooking
 */
export async function upsertUpdatedBooking(updatedBooking: IUpdatedBooking) {
  LogService.info(
    `(compressed) Storing updated bookings into local database, uuid: ${updatedBooking.uuid}, id: ${updatedBooking._id}, syncStatus: ${updatedBooking.syncStatus}`,
    updatedBooking,
    LogLevel.Info
  );
  try {
    await dexieDB.updatedBookings.put(updatedBooking);
  } catch (err: any) {
    LogService.info(
      `Error when storing updated booking into local db, id: ${updatedBooking._id}, syncStatus: ${updatedBooking.syncStatus}`,
      { params: { diffObjects: updatedBooking, err: err } },
      LogLevel.Error
    );
    throw err;
  }
}

/**
 * Sync updated bookings from Dexie to backend
 * Priority is given to Pending bookings, then Failed bookings with fewest attempts
 * @param limit
 */
export async function syncDexieUpdatedBookings(limit: number): Promise<void> {
  let syncedCount = 0;

  while (syncedCount < limit) {
    // Check if database is still open before each iteration
    if (!dexieDB.isOpen()) {
      LogService.info("Database connection closed during sync (PUT), stopping");
      break;
    }

    let bookingToSync: IUpdatedBooking | undefined;

    try {
      // Try to get one Pending booking
      const pending = await dexieDB.updatedBookings
        .where("syncStatus")
        .equals(SyncStatus.Pending)
        .first();

      if (pending) {
        bookingToSync = pending;
      } else {
        // No Pending orders, check if we have Failed ones, ordered by attempts and within retry limit
        const failed = await dexieDB.updatedBookings
          .where("[syncStatus+attempts]")
          .between(
            [SyncStatus.Failed, 0],
            [SyncStatus.Failed, MAX_SYNC_ATTEMPTS]
          )
          .first();
        if (failed) {
          bookingToSync = failed;
        }
      }
    } catch (error) {
      LogService.info(
        `Sync: Error when fetching updated booking from Dexie`,
        error,
        LogLevel.Error
      );
      break;
    }

    // No more updated bookings to sync
    if (!bookingToSync) break;

    try {
      LogService.info(
        `Sync: Going to try sync (PUT) booking with uuid: ${bookingToSync.uuid}, id: ${bookingToSync._id}`
      );
      await syncUpdatedBookings([bookingToSync]);
      LogService.info(
        `Sync: Done syncing (PUT) booking with uuid: ${bookingToSync.uuid}, id: ${bookingToSync._id}`
      );
    } catch (error) {
      LogService.info(
        `Sync: Error syncing (PUT) booking with uuid: ${bookingToSync.uuid}`,
        error,
        LogLevel.Error
      );
    }

    await Timeout.set(1000);
    syncedCount++;
  }
}
